import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox"; // Import Checkbox component
import { ChevronDown, Filter } from "lucide-react";
import { useEffect, useState } from "react";
import CategoryFilter from "./filters/CategoryFilter";
import LocationFilter from "./filters/LocationFilter";
import PriceRangeFilter from "./filters/PriceRangeFilter";
import PropertyTypeFilter from "./filters/PropertyTypeFilter";
import SqftRangeFilter from "./filters/SqftRangeFilter";
import { useListingFilters } from "@/hooks/useListingFilters";
import { ListingType } from "@/lib/types";

interface PropertyFiltersProps {
  initialCategory?: string;
  initialFilters?: {
    categories: string[];
    types: string[];
    locations: string[];
    priceRange: [number, number];
  };
}

const PropertyFilters = ({
  initialCategory = "all",
  initialFilters,
}: PropertyFiltersProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const {
    filters,
    updateFilters,
    clearFilters,
    onSelectProvince,
    onSelectDistrict,
    onSelectCity,
    onClearAllLocations,
  } = useListingFilters();

  // Derive selected locations from the filters.location array
  const selectedProvince = filters.location?.[0] || "";
  const selectedDistrict = filters.location?.[1] || "";
  const selectedCity = filters.location?.[2] || "";

  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialFilters?.categories?.length
      ? initialFilters.categories
      : initialCategory !== "all"
      ? [initialCategory]
      : (filters.listing_type as string[] | undefined) || []
  );
  const [selectedTypes, setSelectedTypes] = useState<string[] | ListingType[]>(
    []
  );

  // New state for featured filter
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(
    filters.featured || false
  );

  useEffect(() => {
    setSelectedTypes(filters.propertyType || []);
  }, [filters.propertyType]);

  // Synchronize showFeaturedOnly with filters.featured from the hook
  useEffect(() => {
    setShowFeaturedOnly(filters.featured || false);
  }, [filters.featured]);

  const [priceRange, setPriceRange] = useState<[number, number]>(
    initialFilters?.priceRange || [
      filters.minPrice || 0,
      filters.maxPrice || 50000000,
    ]
  );

  const [sqftRange, setSqftRange] = useState<[number, number]>([
    filters.minSize || 0,
    filters.maxSize || undefined, // Changed to undefined
  ]);

  useEffect(() => {
    setSqftRange([filters.minSize || 0, filters.maxSize || undefined]); // Changed to undefined
  }, [filters.minSize, filters.maxSize]);

  const handleToggleCategory = (category: string) => {
    const newCategories = selectedCategories.includes(category)
      ? selectedCategories.filter((c) => c !== category)
      : [...selectedCategories, category];
    setSelectedCategories(newCategories);
    const validListingTypes = newCategories.filter((cat): cat is ListingType =>
      ["sale", "rent", "lease"].includes(cat)
    );
    updateFilters({ listing_type: validListingTypes as any });
  };

  const handleClearCategories = () => {
    setSelectedCategories([]);
    updateFilters({ listing_type: [] as ListingType[] });
  };

  const handleToggleType = (type: ListingType) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];
    setSelectedTypes(newTypes);
    updateFilters({ propertyType: newTypes });
  };

  const handleClearTypes = () => {
    setSelectedTypes([]);
    updateFilters({ propertyType: [] });
  };

  const handlePriceChange = (min: number, max: number) => {
    setPriceRange([min, max]);
    updateFilters({ minPrice: min, maxPrice: max });
  };

  const handleSqftChange = (min: number, max: number) => {
    setSqftRange([min, max]);
    updateFilters({ minSize: min, maxSize: max });
  };

  // New handler for featured filter
  const handleFeaturedChange = (checked: boolean) => {
    setShowFeaturedOnly(checked);
    updateFilters({ featured: checked || undefined }); // Set to undefined if false to remove from URL
  };

  useEffect(() => {
    console.log("[PropertyFilters] Current filter state (from hook):", filters);
  }, [filters]);

  const handleClearAll = () => {
    setSelectedCategories([]);
    setSelectedTypes([]);
    setPriceRange([0, 50000000]);
    setShowFeaturedOnly(false); // Clear featured filter
    clearFilters();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden sticky top-20">
      <div className="md:hidden p-4 border-b border-gray-200">
        <Button
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          variant="outline"
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center">
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </div>
          <ChevronDown
            className={`w-4 h-4 transition-transform ${
              isFiltersOpen ? "rotate-180" : ""
            }`}
          />
        </Button>
      </div>

      <div className={`${isFiltersOpen ? "block" : "hidden"} md:block`}>
        <CategoryFilter
          selectedCategories={selectedCategories}
          onToggleCategory={handleToggleCategory}
          onClearCategories={handleClearCategories}
        />

        <PropertyTypeFilter
          selectedTypes={selectedTypes}
          onToggleType={handleToggleType}
          onClearTypes={handleClearTypes}
        />

        <LocationFilter
          selectedProvince={selectedProvince}
          onSelectProvince={onSelectProvince}
          selectedDistrict={selectedDistrict}
          onSelectDistrict={onSelectDistrict}
          selectedCity={selectedCity}
          onSelectCity={onSelectCity}
          onClearAllLocations={onClearAllLocations}
          key={`${selectedProvince}-${selectedDistrict}-${selectedCity}`}
        />

        <PriceRangeFilter
          min={0}
          max={50000000}
          step={100000}
          initialMin={priceRange[0]}
          initialMax={priceRange[1]}
          onPriceChange={handlePriceChange}
        />

        <SqftRangeFilter
          min={0}
          max={75000}
          step={50}
          initialMin={sqftRange[0]}
          initialMax={sqftRange[1]}
          onSqftChange={handleSqftChange}
        />

        {/* New Featured Properties Filter */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="featured-only"
              checked={showFeaturedOnly}
              onCheckedChange={handleFeaturedChange}
            />
            <label
              htmlFor="featured-only"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Show Featured Only
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyFilters;
